import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Building2, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { data, Link, useSearchParams } from "react-router";
import { BreadcrumbJsonLd } from "~/components/seo/breadcrumb-jsonld";
import { Skeleton } from "~/components/ui/skeleton";
import { companySlug } from "~/lib/company-profile";
import { envConfig } from "~/lib/env-validation";
import { generateMetaTags, SITE_URL } from "~/lib/seo";
import type { Route } from "./+types/kraj-detail";

// --- Types ---

interface CompanyListItem {
	ico: string;
	name: string;
	city: string | null;
	status: string;
}

interface RegionCompaniesResponse {
	companies: CompanyListItem[];
	total: number;
	page: number;
	limit: number;
	regionSlug: string;
	regionName: string;
}

// --- Region name helpers ---

const REGION_LOCATIVE: Record<string, string> = {
	praha: "v Praze",
	stredocesky: "ve Středočeském kraji",
	jihocesky: "v Jihočeském kraji",
	plzensky: "v Plzeňském kraji",
	karlovarsky: "v Karlovarském kraji",
	ustecky: "v Ústeckém kraji",
	liberecky: "v Libereckém kraji",
	kralovehradecky: "v Královéhradeckém kraji",
	pardubicky: "v Pardubickém kraji",
	vysocina: "na Vysočině",
	jihomoravsky: "v Jihomoravském kraji",
	olomoucky: "v Olomouckém kraji",
	zlinsky: "ve Zlínském kraji",
	moravskoslezsky: "v Moravskoslezském kraji",
};

// --- Status labels ---

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
	ACTIVE: { label: "Aktivní", className: "badge badge-soft badge-success" },
	DISSOLVED: { label: "Zrušená", className: "badge badge-soft badge-error" },
	IN_LIQUIDATION: { label: "V likvidaci", className: "badge badge-soft badge-warning" },
	IN_INSOLVENCY: { label: "V insolvenci", className: "badge badge-soft badge-error" },
	SUSPENDED: { label: "Pozastavená", className: "badge badge-soft badge-warning" },
};

// --- Loader ---

export async function loader({ params }: Route.LoaderArgs) {
	const { regionSlug } = params;
	const res = await fetch(
		`${envConfig.apiBaseUrl}/directory/regions/${encodeURIComponent(regionSlug)}?page=1&limit=20`,
	);

	if (res.status === 404) {
		throw data(null, { status: 404 });
	}
	if (!res.ok) {
		throw data(null, { status: 500 });
	}

	const result: RegionCompaniesResponse = await res.json();
	return { initial: result, regionSlug };
}

// --- Meta ---

export function meta({ data: loaderData }: Route.MetaArgs) {
	if (!loaderData) {
		return [
			{ title: "Kraj nenalezen | FirmIndex" },
			{ name: "robots", content: "noindex, nofollow" },
		];
	}

	const { initial, regionSlug } = loaderData;
	const locative = REGION_LOCATIVE[regionSlug] ?? `v regionu ${initial.regionName}`;
	const title = `Firmy ${locative} | FirmIndex`;
	const description = `Seznam firem ${locative}. Celkem ${initial.total.toLocaleString("cs-CZ")} firem v tomto kraji.`;

	return generateMetaTags({
		title,
		description,
		canonical: `${SITE_URL}/directory/kraj/${regionSlug}`,
	});
}

// --- Fetch function ---

const PAGE_LIMIT = 20;

async function fetchRegionCompanies(slug: string, page: number): Promise<RegionCompaniesResponse> {
	const url = new URL(`/directory/regions/${encodeURIComponent(slug)}`, envConfig.apiBaseUrl);
	url.searchParams.set("page", String(page));
	url.searchParams.set("limit", String(PAGE_LIMIT));

	const res = await fetch(url.toString());
	if (!res.ok) throw new Error("Chyba při načítání firem");
	return res.json();
}

// --- Components ---

function CompanyRow({ company }: { company: CompanyListItem }) {
	const status = STATUS_LABELS[company.status] ?? {
		label: company.status,
		className: "badge badge-soft",
	};

	return (
		<Link
			to={`/firma/${company.ico}-${companySlug(company.name)}`}
			className="flex items-center justify-between gap-3 card-surface rounded-xl p-4 transition-shadow hover:shadow-md"
		>
			<div className="flex flex-col gap-1 min-w-0">
				<h3 className="text-sm font-semibold truncate">{company.name}</h3>
				<div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
					<span className="font-mono">IČO: {company.ico}</span>
					{company.city && (
						<span className="flex items-center gap-1">
							<MapPin className="h-3 w-3" />
							{company.city}
						</span>
					)}
				</div>
			</div>
			<span className={status.className}>{status.label}</span>
		</Link>
	);
}

function ResultSkeleton() {
	return (
		<div className="flex items-center justify-between gap-3 card-surface rounded-xl p-4">
			<div className="flex flex-col gap-1">
				<Skeleton className="h-4 w-48" />
				<Skeleton className="h-3 w-32" />
			</div>
			<Skeleton className="h-5 w-16 rounded-full" />
		</div>
	);
}

// --- Page ---

export default function KrajDetailPage({ loaderData }: Route.ComponentProps) {
	const { initial, regionSlug } = loaderData;
	const [searchParams, setSearchParams] = useSearchParams();
	const page = Number(searchParams.get("page") ?? "1");

	const locative = REGION_LOCATIVE[regionSlug] ?? `v regionu ${initial.regionName}`;

	const { data: pageData, status } = useQuery({
		queryKey: ["regionCompanies", regionSlug, page],
		queryFn: () => fetchRegionCompanies(regionSlug, page),
		initialData: page === 1 ? initial : undefined,
	});

	const totalPages = pageData ? Math.ceil(pageData.total / PAGE_LIMIT) : 0;

	const goToPage = (newPage: number) => {
		setSearchParams({ page: String(newPage) });
		window.scrollTo(0, 0);
	};

	return (
		<>
			<BreadcrumbJsonLd
				items={[
					{ name: "FirmIndex", url: "/" },
					{ name: "Kraje", url: "/directory/kraj" },
					{ name: initial.regionName, url: `/directory/kraj/${regionSlug}` },
				]}
			/>

			<div className="bg-background">
				{/* Header */}
				<div className="border-b border-gray-200 bg-card">
					<div className="mx-auto max-w-4xl px-4 py-8">
						<nav className="mb-3 text-sm text-muted-foreground">
							<Link to="/directory/kraj" className="hover:text-primary transition-colors">
								Kraje
							</Link>
							<span className="mx-2">/</span>
							<span>{initial.regionName}</span>
						</nav>
						<h1 className="text-2xl font-bold sm:text-3xl">Firmy {locative}</h1>
						<p className="mt-2 text-muted-foreground">
							Celkem <strong>{initial.total.toLocaleString("cs-CZ")}</strong>{" "}
							{initial.total === 1 ? "firma" : initial.total < 5 ? "firmy" : "firem"} {locative}.
						</p>
					</div>
				</div>

				{/* Content */}
				<main className="mx-auto max-w-4xl px-4 py-6">
					<div className="flex flex-col gap-3">
						{/* Loading */}
						{status === "pending" &&
							["s1", "s2", "s3", "s4", "s5"].map((key) => <ResultSkeleton key={key} />)}

						{/* Error */}
						{status === "error" && (
							<div className="flex flex-col items-center gap-2 py-16 text-center">
								<AlertCircle className="h-12 w-12 text-destructive" />
								<p className="text-sm font-medium">Nepodařilo se načíst data</p>
							</div>
						)}

						{/* Results */}
						{pageData &&
							pageData.companies.length > 0 &&
							pageData.companies.map((company) => (
								<CompanyRow key={company.ico} company={company} />
							))}

						{/* Empty state */}
						{pageData && pageData.companies.length === 0 && (
							<div className="flex flex-col items-center gap-2 py-16 text-center">
								<Building2 className="h-12 w-12 text-muted-foreground" />
								<p className="text-sm font-medium">V tomto kraji zatím nejsou žádné firmy</p>
							</div>
						)}

						{/* Pagination */}
						{totalPages > 1 && (
							<div className="flex items-center justify-center gap-2 py-4">
								<button
									type="button"
									className="btn btn-ghost btn-sm"
									disabled={page <= 1}
									onClick={() => goToPage(page - 1)}
								>
									<ChevronLeft className="h-4 w-4" />
									Předchozí
								</button>
								<span className="text-sm text-muted-foreground">
									Strana {page} z {totalPages}
								</span>
								<button
									type="button"
									className="btn btn-ghost btn-sm"
									disabled={page >= totalPages}
									onClick={() => goToPage(page + 1)}
								>
									Další
									<ChevronRight className="h-4 w-4" />
								</button>
							</div>
						)}
					</div>
				</main>
			</div>
		</>
	);
}
